import {
  DateInput,
  TextArea,
  TextInput,
  TimeInput,
  WithErrorForm,
} from "components/common";
import { Modal, ModalProps } from "components/common/Modal";
import { Button } from "components/parts/Button/Button";
import { SelectBox } from "components/parts/Input/SelectBox";
import { Typography } from "components/parts/Typography/Typography";
import { useFormik } from "formik";
import { DisplayInterview } from "pages/InterviewManagement/hook";
import { useConsoleState } from "providers/ConsoleStateProvider";
import { useLoadingState } from "providers/LoadingProvider";
import React from "react";
import { useHistory } from "react-router";
import {
  CreateInterviewInput,
  DeleteInterviewInput,
  InterviewType,
  Subject,
  UpdateInterviewInput,
} from "types/graphqlapi";
import { interviewPulldownList } from "types/status";

import {
  createInterviewMutation,
  deleteInterviewMutation,
  listInterviewsQuery,
  updateInterviewMutation,
} from "utils/apis/interview";
import {
  getDisplayTermInterviewYMD,
  getDisplayYMD,
  getEndDate,
  getEndTime,
  getInterviewMinutes,
  getYMDStr,
  isInterviewStarted,
} from "utils/common";
import { validationSchemaInterview } from "utils/validation";

import {
  StyledButtonArea,
  StyledRightColumn,
  StyledForm,
  StyledFormRow,
  StyledModalContent,
  StyledModalFooter,
  StyledModalHeader,
  StyledInputWithSelect,
} from "./styled";
import { getSubjectQuery, updateSubjectMutation } from "utils/apis/subject";

type InterviewManageModalProps = {
  interviewList: DisplayInterview[];
  selectedSubjectId: string;
  selectedInterview: DisplayInterview;
  clearSelectedInterview: () => void;
  selectInterviewDetail: (interview: DisplayInterview | undefined) => void;
} & ModalProps;

type InterviewFormValueType = {
  scheduled_start_date: Date;
  scheduled_start_time: string;
  interview_minutes: number;
  scheduled_end_date?: Date;
  scheduled_end_time?: string;
  canceled: number;
  memo: string | null;
};

const INTERVIEW_MINUTES_OPTION_LIST = [
  { value: "", text: "-" },
  { value: 15, text: "15" },
  { value: 30, text: "30" },
  { value: 45, text: "45" },
  { value: 60, text: "60" },
  { value: 75, text: "75" },
  { value: 90, text: "90" },
  { value: 105, text: "105" },
  { value: 120, text: "120" },
];

export const InterviewManageModal: React.FC<InterviewManageModalProps> = ({
  interviewList,
  closeHandler,
  selectedSubjectId,
  selectedInterview,
  clearSelectedInterview,
  selectInterviewDetail,
}) => {
  const history = useHistory();
  const { setLoading } = useLoadingState();
  const { selectedGroupId, selectedSubjectType } = useConsoleState();
  const [subject, setSubject] = React.useState<Subject | undefined>();

  const maxDate = subject?.input_expiration_date
    ? new Date(subject.input_expiration_date)
    : undefined;
  maxDate && maxDate.setDate(maxDate.getDate() - 1);
  const insertInterview = async (
    values: InterviewFormValueType
  ): Promise<void> => {
    try {
      const createInterviewInput: CreateInterviewInput = {
        subject_id: selectedSubjectId,
        interview_seq: 0,
        scheduled_start_date: values.scheduled_start_date
          ? getYMDStr(new Date(values.scheduled_start_date))
          : null,
        scheduled_start_time: values.scheduled_start_time,
        ...(selectedSubjectType === InterviewType.TERM
          ? {
              scheduled_end_date: values.scheduled_end_date
                ? getYMDStr(new Date(values.scheduled_end_date))
                : null,
              scheduled_end_time: values.scheduled_end_time,
            }
          : {
              scheduled_end_date: getEndDate(
                values.scheduled_start_date,
                values.scheduled_start_time,
                values.interview_minutes
              ),
              scheduled_end_time: getEndTime(
                values.scheduled_start_date,
                values.scheduled_start_time,
                values.interview_minutes
              ),
            }),
        interview_type: selectedSubjectType,
        actual_start_date: null,
        actual_end_date: null,
        actual_start_time: null,
        actual_end_time: null,
        memo: values.memo,
        canceled: 0,
        groups: selectedGroupId,
      };
      await createInterviewMutation(createInterviewInput);
      await updateSubjectExpirationDate();
      clearSelectedInterview();
    } catch (e) {
      console.error(e);
      alert("エラーが発生しました。");
      history.replace("/auth/login");
    } finally {
      setLoading(false);
    }
  };

  const updateInterview = async (
    values: InterviewFormValueType
  ): Promise<void> => {
    try {
      const updateSubjectInput: UpdateInterviewInput = {
        id: selectedInterview.id,
        scheduled_start_date: values.scheduled_start_date
          ? getYMDStr(new Date(values.scheduled_start_date))
          : null,
        scheduled_start_time: values.scheduled_start_time,
        ...(selectedSubjectType === InterviewType.TERM
          ? {
              scheduled_end_date: values.scheduled_end_date
                ? getYMDStr(new Date(values.scheduled_end_date))
                : null,
              scheduled_end_time: values.scheduled_end_time,
            }
          : {
              scheduled_end_date: getEndDate(
                values.scheduled_start_date,
                values.scheduled_start_time,
                values.interview_minutes
              ),
              scheduled_end_time: getEndTime(
                values.scheduled_start_date,
                values.scheduled_start_time,
                values.interview_minutes
              ),
            }),
        canceled: values.canceled,
        memo: values.memo,
      };
      await updateInterviewMutation(updateSubjectInput).then(async (result) => {
        const updateResultInterview = result.data?.updateInterview;
        const updateResultDisplayInterview = {
          ...selectedInterview,
          ...updateResultInterview,
        };

        selectInterviewDetail(updateResultDisplayInterview);
      });
      await updateSubjectExpirationDate();
      clearSelectedInterview();
    } catch (e) {
      console.error(e);
      alert("エラーが発生しました。");
      history.replace("/auth/login");
    } finally {
      setLoading(false);
    }
  };

  const updateSubjectExpirationDate = async () => {
    if (subject && !subject?.input_expiration_date) {
      const currentInterviewList = await listInterviewsQuery(subject.id);
      const scheduledEndDates = currentInterviewList.map(
        (interview) => new Date(interview.scheduled_end_date as string)
      );

      const latestEndDate = scheduledEndDates.reduce(
        (latestDate, currentDate) => {
          return currentDate > latestDate ? currentDate : latestDate;
        }
      );

      if (latestEndDate) {
        const latestExpirationDate = new Date(latestEndDate);
        latestExpirationDate.setDate(latestEndDate.getDate() + 90);

        const currentExpirationDate = new Date(
          subject.expiration_date as string
        );

        if (
          latestExpirationDate &&
          latestExpirationDate.getTime() !== currentExpirationDate.getTime()
        ) {
          await updateSubjectMutation({
            id: selectedSubjectId,
            expiration_date: getYMDStr(latestExpirationDate),
          });
        }
      }
    }
  };

  const clickDeleteButtonHandler: React.FormEventHandler<
    HTMLButtonElement
  > = async () => {
    try {
      const res = confirm("選択されたチャットルームを削除します");
      if (res) {
        setLoading(true);
        const deleteSubjectInput: DeleteInterviewInput = {
          id: selectedInterview.id,
        };
        await deleteInterviewMutation(deleteSubjectInput);
        await updateSubjectExpirationDate();
        clearSelectedInterview();
        selectInterviewDetail(undefined);
      }
    } catch (e) {
      console.error(e);
      alert("エラーが発生しました。");
      history.replace("/auth/login");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (values: InterviewFormValueType) => {
    setLoading(true);
    selectedInterview.id ? updateInterview(values) : insertInterview(values);
  };

  const decideInitialFormikValue = (): InterviewFormValueType => {
    const initValue = {
      scheduled_start_date: selectedInterview.scheduled_start_date
        ? new Date(selectedInterview.scheduled_start_date)
        : new Date(Date.now()),
      scheduled_start_time: selectedInterview?.scheduled_start_time || "",
      interview_minutes: selectedInterview.id
        ? getInterviewMinutes(
            selectedInterview.scheduled_start_date as string,
            selectedInterview.scheduled_start_time as string,
            selectedInterview.scheduled_end_date as string,
            selectedInterview.scheduled_end_time as string
          )
        : 60,
      scheduled_end_date: selectedInterview.scheduled_end_date
        ? new Date(selectedInterview.scheduled_end_date)
        : new Date(Date.now()),
      scheduled_end_time: selectedInterview?.scheduled_end_time || "",
      canceled: selectedInterview.canceled || 0,
      memo: selectedInterview.memo || "",
    };
    return initValue;
  };

  const formik = useFormik<InterviewFormValueType>({
    initialValues: decideInitialFormikValue(),
    validationSchema: validationSchemaInterview(
      selectedSubjectType as InterviewType,
      subject as Subject
    ),
    onSubmit: onSubmit,
  });

  const [optionMinitus, setOptionMinutes] = React.useState<number>();

  React.useEffect(() => {
    (async () => {
      const subject = await getSubjectQuery(selectedSubjectId);
      setSubject(subject);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal closeHandler={closeHandler}>
      <StyledForm onSubmit={formik.handleSubmit}>
        <StyledModalHeader>
          {selectedInterview.id ? (
            <Typography text="チャットルーム修正" size="large" />
          ) : (
            <Typography text="新規チャットルーム登録" size="large" />
          )}
        </StyledModalHeader>
        <StyledModalContent>
          {selectedInterview.id && (
            <StyledFormRow>
              <Typography text="No" />
              <StyledRightColumn>
                <Typography text={`${selectedInterview.interview_seq}`} />
              </StyledRightColumn>
            </StyledFormRow>
          )}
          <StyledFormRow>
            <Typography
              text="開始予定日時"
              required={!isInterviewStarted(selectedInterview)}
            />
            <WithErrorForm
              touched={
                (formik.touched.scheduled_start_date as boolean) ||
                formik.touched.scheduled_start_time
              }
              errorMessage={
                (formik.errors.scheduled_start_date as string)
                  ? (formik.errors.scheduled_start_date as string)
                  : formik.errors.scheduled_start_time
              }
            >
              <StyledRightColumn>
                <DateInput
                  autoFocus={!selectedInterview.id}
                  name="scheduled_start_date"
                  date={
                    formik.values.scheduled_start_date
                      ? new Date(formik.values.scheduled_start_date)
                      : new Date(Date.now())
                  }
                  readonly={isInterviewStarted(selectedInterview)}
                  onChangeHandler={(date) =>
                    formik.setFieldValue("scheduled_start_date", date)
                  }
                  onBlurHandler={() =>
                    formik.setFieldTouched("scheduled_start_date", true, true)
                  }
                  width="10rem"
                  maxDate={maxDate}
                />
                <TimeInput
                  {...formik.getFieldProps("scheduled_start_time")}
                  readonly={isInterviewStarted(selectedInterview)}
                />
              </StyledRightColumn>
            </WithErrorForm>
          </StyledFormRow>

          {selectedSubjectType === InterviewType.TERM ? (
            <StyledFormRow>
              <Typography
                text="終了予定日時"
                required={!selectedInterview.actual_end_date}
              />
              <WithErrorForm
                touched={
                  (formik.touched.scheduled_end_date as boolean) ||
                  formik.touched.scheduled_end_time
                }
                errorMessage={
                  (formik.errors.scheduled_end_date as string)
                    ? (formik.errors.scheduled_end_date as string)
                    : formik.errors.scheduled_end_time
                }
              >
                <StyledRightColumn>
                  <DateInput
                    name="scheduled_end_date"
                    date={
                      formik.values.scheduled_end_date
                        ? new Date(formik.values.scheduled_end_date)
                        : new Date(Date.now())
                    }
                    readonly={selectedInterview.actual_end_date ? true : false}
                    onChangeHandler={(date) =>
                      formik.setFieldValue("scheduled_end_date", date)
                    }
                    onBlurHandler={() =>
                      formik.setFieldTouched("scheduled_end_date", true, true)
                    }
                    width="10rem"
                    maxDate={maxDate}
                  />
                  <TimeInput
                    {...formik.getFieldProps("scheduled_end_time")}
                    readonly={selectedInterview.actual_end_date ? true : false}
                  />
                </StyledRightColumn>
              </WithErrorForm>
            </StyledFormRow>
          ) : (
            <StyledFormRow>
              <Typography
                text="インタビュー時間"
                required={!isInterviewStarted(selectedInterview)}
              />
              <WithErrorForm
                touched={formik.touched.interview_minutes}
                errorMessage={formik.errors.interview_minutes}
              >
                <StyledRightColumn>
                  <StyledInputWithSelect>
                    <TextInput
                      {...formik.getFieldProps("interview_minutes")}
                      width="3.8rem"
                      type="number"
                      min={0}
                      readOnly={isInterviewStarted(selectedInterview)}
                    />

                    <SelectBox
                      value={
                        optionMinitus === formik.values.interview_minutes
                          ? optionMinitus
                          : ""
                      }
                      onChange={(e) => {
                        setOptionMinutes(Number(e.target.value));
                        if (e.target.value) {
                          formik.setFieldValue(
                            "interview_minutes",
                            Number(e.target.value)
                          );
                        }
                      }}
                      optionList={INTERVIEW_MINUTES_OPTION_LIST}
                      width="0px"
                      disabled={isInterviewStarted(selectedInterview)}
                    />
                  </StyledInputWithSelect>
                  <Typography text="分" />
                </StyledRightColumn>
              </WithErrorForm>
            </StyledFormRow>
          )}

          {selectedInterview.actual_end_date && (
            <StyledFormRow>
              <Typography text="チャット実施日時" />
              <StyledRightColumn>
                <Typography
                  text={
                    selectedInterview.interview_type === InterviewType.TERM
                      ? `${getDisplayTermInterviewYMD(
                          selectedInterview.actual_start_date as string,
                          selectedInterview.actual_start_time as string,
                          selectedInterview.actual_end_date as string,
                          selectedInterview.actual_end_time as string
                        )}`
                      : `${getDisplayYMD(
                          new Date(selectedInterview.actual_end_date)
                        )} ${selectedInterview.actual_start_time}～${
                          selectedInterview.actual_end_time
                        }`
                  }
                />
              </StyledRightColumn>
            </StyledFormRow>
          )}

          {selectedInterview.id && (
            <StyledFormRow>
              <Typography text="キャンセル" />
              <SelectBox
                name="canceled"
                value={formik.values.canceled}
                optionList={interviewPulldownList}
                width="21.5rem"
                onChange={(e) => {
                  formik.setFieldValue("canceled", e.target.value);
                }}
              />
            </StyledFormRow>
          )}

          <StyledFormRow>
            <Typography text="備考" />
            <WithErrorForm
              touched={formik.touched.memo}
              errorMessage={formik.errors.memo}
            >
              <TextArea
                {...formik.getFieldProps("memo")}
                autoFocus={selectedInterview.id ? true : false}
                width="21.5rem"
                maxlength="150"
              />
            </WithErrorForm>
          </StyledFormRow>
        </StyledModalContent>
        <StyledModalFooter>
          <StyledButtonArea>
            {selectedInterview.id && !isInterviewStarted(selectedInterview) && (
              <Button
                onClick={clickDeleteButtonHandler}
                variant="secondary"
                text="削除"
                type="button"
              />
            )}
            <Button
              text={selectedInterview.id ? "更新" : "登録"}
              type="submit"
            />
          </StyledButtonArea>
        </StyledModalFooter>
      </StyledForm>
    </Modal>
  );
};
